/**
 * Vector Drift's server-timed races (act-babylon DR-0093; ACT HQ BATTLE-PASS.md §3).
 * The home game server stamps a race's start, each lap and the finish on its own
 * clock, so a race can earn battle-pass XP and count on the ACT account even though
 * the race itself runs in the browser. HTTP contracts between
 * vectordrift.actuniverse.com and the home server; the races are kept in `vd_races`.
 */
import type { PassAwardMessage } from "./pass";

/** Every Vector Drift race is three laps (driftracer's RACE_LAPS). */
export const VD_RACE_LAPS = 3;

/** Quick Race (against 11 bots) or Time Attack (alone, against a ghost). */
export type VdRaceMode = "quick" | "time";

/** POST /vectordrift/race/start — at GO. The ticket names the race in every later call. */
export type VdRaceStartRequest = { course: string; ship: string; mode: VdRaceMode };
export type VdRaceStartResponse = { ticket: string; laps: number };

/**
 * POST /vectordrift/race/lap — a lap crossed (`lap` counts from 1). The steering
 * proof is a soft hint from the client: how often the steer input changed side,
 * and how long any input was held. A lap without steering still counts as time.
 */
export type VdRaceLapRequest = { ticket: string; lap: number; steerFlips: number; inputMs: number };
/** `lapMs`: the lap on the server's clock. */
export type VdRaceLapResponse = { lap: number; lapMs: number };

/**
 * POST /vectordrift/race/finish — after the last lap. `place` (Quick Race, against
 * the bots) is recorded as the client says but never trusted: the bots run in the
 * browser.
 */
export type VdRaceFinishRequest = { ticket: string; place?: number | null };
/**
 * `recorded`: the race is on the account (false while the database can't take it).
 * `pass`: the battle pass's award for its laps, for the results screen's XP fill (ACT HQ
 * t-1104); null when nothing was paid or the award didn't come back in time (it still
 * lands), and absent from servers before t-1104.
 */
export type VdRaceFinishResponse = { recorded: boolean; totalMs: number; lapsMs: number[]; idleLaps: number; pass?: PassAwardMessage | null };

/**
 * POST /vectordrift/race/quit — the player left the race before its end. Its
 * checked laps, if any, are kept on the account as an unfinished race (as they are
 * when a race is left open until it expires).
 */
export type VdRaceQuitRequest = { ticket: string };
export type VdRaceQuitResponse = { recorded: boolean; lapsMs: number[] };

export type VdRaceErrorCode =
  | "unauthenticated"
  | "origin_rejected"
  | "invalid_request"
  | "unknown_course"
  | "unknown_ticket"
  | "rate_limited"
  | "lap_out_of_order"
  | "lap_too_fast"
  | "not_finished"
  | "already_finished"
  | "expired"
  | "unavailable"
  | "server_error";

// ---- Online races (ACT HQ t-492, act-babylon DR-0094) ----------------------
// One quick-join race per room on the home server (Theo, 10 Oct 2026: "one quick race ...
// until we have a player base, I don't really want to establish an ELO system yet"). Each
// device drives its own craft; the room relays positions, sets the start and stamps laps
// and places; the first person's device runs the bots and sends them as its own.

/** Bumped whenever the room's messages change: an older page reloads first. */
export const VD_ONLINE_PROTOCOL = 1;
/** Racers on the grid, people first, bots filling the rest. */
export const VD_ONLINE_SEATS = 12;
/** How long a race waits for people before the bots fill it (ms). */
export const VD_ONLINE_FILL_MS = 15_000;
/** From the grid filling to GO (ms): the 3-2-1, with room for slow pages. */
export const VD_ONLINE_COUNTDOWN_MS = 5_000;

export type VdOnlineJoinOptions = {
  authToken?: string;
  displayName?: string;
  /** VD_ONLINE_PROTOCOL of the page. */
  protocol?: number;
  /** The craft the player picked (driftracer's ship id, "vd-01".."vd-07"). */
  ship?: string;
};

/** waiting (for people) → countdown (grid full, to GO) → racing → results. */
export type VdOnlinePhase = "waiting" | "countdown" | "racing" | "results";

export type VdOnlineRacer = {
  /** The racer's grid slot, 0 at pole: also its id in every message. */
  slot: number;
  name: string;
  ship: string;
  bot: boolean;
  /** Laps the room has stamped. */
  laps: number;
  /** Race time at the finish (ms, from GO), or null while racing. */
  finishMs: number | null;
  /** The finishing place, or null until the results. */
  place: number | null;
  /** A person who left the race (their craft is gone). */
  left: boolean;
};

/** The room's slow state, sent to everyone on every change. Times are the server's clock (ms since 1970). */
export type VdOnlineRacePayload = {
  phase: VdOnlinePhase;
  course: string;
  laps: number;
  /** When the waiting ends and bots fill the grid. */
  fillAt: number;
  /** GO, once the grid is full. */
  startAt: number | null;
  /** The server's clock when this was sent. */
  now: number;
  racers: VdOnlineRacer[];
  /** Your own slot. */
  you: number;
  /** The slot of the person whose device runs the bots. */
  botHost: number | null;
};

/** A lap crossed: a person's own slot, or one of the bots from the bot host. */
export type VdOnlineLapPayload = { slot: number; lap: number };
/** Clock sync: the page sends its time, the room answers with its own. */
export type VdOnlinePingPayload = { t: number };
export type VdOnlinePongPayload = { t: number; server: number };

export const VD_ONLINE_MSG = {
  /** room → everyone: VdOnlineRacePayload. */
  race: "vd:race",
  /** page → room: packed craft states (vdOnline.ts); room → everyone else: the same, checked. */
  state: "vd:state",
  /** page → room: VdOnlineLapPayload. */
  lap: "vd:lap",
  /** page → room: VdOnlinePingPayload; room → page: VdOnlinePongPayload. */
  ping: "vd:ping",
} as const;
